import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Input, Link, Modal, SectionForm, Select, Text } from '@Sage/hoi-poi-ui';

import Context from 'managers/Context';
import ModalHoi from 'components/ModalHoi';
import SelectHoi from 'components/SelectHoi';
import Map from 'components/Map';
import GeoLocationFieldMarker from 'containers/components/Fields/GeoLocation/GeoLocationFieldMarker';
import { PATH_IMAGE } from 'constants/Environment';
import { REG_EX_EMAIL } from 'constants/Constants';
import { getLiteral } from 'utils/getLiteral';
import { getFMAddressFromCoordinates, getFMAddressFromGeoCodeResults } from 'utils/map';
import { formatAddressFromGeocoder, shouldFormatAddressAsAmericanIso } from 'utils/addresses';

import './styles.scss';

const FREE_EMAIL_DOMAINS = new Set([
    'gmail.com',
    'outlook.com',
    'yahoo.com',
    'hotmail.com',
    'live.com',
    'icloud.com',
]);

const INITIAL_FORM_STATE = {
    fromName: '',
    fromEmail: '',
    replyTo: '',
    nickname: '',
    address: '',
    addressDetail: '',
    city: '',
    region: '',
    postalCode: '',
    idCountry: null,
    country: '',
    latitude: null,
    longitude: null,
};

const REQUIRED_FIELDS = [
    'fromName',
    'fromEmail',
    'replyTo',
    'nickname',
    'address',
    'city',
    'postalCode',
    'idCountry',
];

const SenderFormModal = ({ isOpen, onCancel, onCreate, onRequestClose }) => {
    const mapComponentRef = useRef(null);
    const markerComponentRef = useRef(null);

    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [errors, setErrors] = useState({});
    const [countryOptions, setCountryOptions] = useState([]);
    const [countriesRaw, setCountriesRaw] = useState([]);

    const [isGeoLocationModalOpen, setIsGeoLocationModalOpen] = useState(false);
    const [locationInput, setLocationInput] = useState('');
    const [defaultOptions, setDefaultOptions] = useState([]);
    const [locationValue, setLocationValue] = useState(null);
    const [positionMarker, setPositionMarker] = useState(null);
    const [addressFields, setAddressFields] = useState(null);
    const [fullAddress, setFullAddress] = useState(null);
    const [isMapPositionUpdated, setIsMapPositionUpdated] = useState(false);

    useEffect(() => {
        Context.serverListManager.getList('tblCountries').then((list = []) => {
            setCountriesRaw(list);
            setCountryOptions(
                list.map((item) => ({
                    label: item.label,
                    value: String(item.value),
                })),
            );
        });
    }, []);

    useEffect(() => {
        if (isOpen) return;
        setFormData(INITIAL_FORM_STATE);
        setErrors({});
        setIsGeoLocationModalOpen(false);
        setLocationInput('');
        setDefaultOptions([]);
        setLocationValue(null);
        setPositionMarker(null);
        setAddressFields(null);
        setFullAddress(null);
        setIsMapPositionUpdated(false);
    }, [isOpen]);

    const fromEmailDomain = useMemo(() => {
        const normalized = formData.fromEmail?.trim().toLowerCase();
        if (!normalized || !normalized.includes('@')) return null;
        const [, domain] = normalized.split('@');
        return domain || null;
    }, [formData.fromEmail]);

    const isFreeEmailDomain = useMemo(
        () => !!fromEmailDomain && FREE_EMAIL_DOMAINS.has(fromEmailDomain),
        [fromEmailDomain],
    );

    const getRequiredError = useCallback((value) => {
        if (value === null || value === undefined || value === '') {
            return getLiteral('helptext_required_field');
        }
        return null;
    }, []);

    const validateEmail = useCallback((value) => {
        if (!value) return null;
        return REG_EX_EMAIL.test(value.trim()) ? null : getLiteral('label_invalid_email');
    }, []);

    const validateField = useCallback(
        (field, value) => {
            if (REQUIRED_FIELDS.includes(field)) {
                const requiredError = getRequiredError(value);
                if (requiredError) return requiredError;
            }

            if (field === 'fromEmail' || field === 'replyTo') {
                return validateEmail(value);
            }

            return null;
        },
        [getRequiredError, validateEmail],
    );

    const validateForm = useCallback(() => {
        const nextErrors = {};

        Object.entries(formData).forEach(([field, value]) => {
            const fieldError = validateField(field, value);
            if (fieldError) nextErrors[field] = fieldError;
        });

        REQUIRED_FIELDS.forEach((field) => {
            if (!Object.prototype.hasOwnProperty.call(nextErrors, field)) {
                const fieldError = validateField(field, formData[field]);
                if (fieldError) nextErrors[field] = fieldError;
            }
        });

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }, [formData, validateField]);

    const updateField = useCallback(
        (field) => (value) => {
            setFormData((prev) => ({ ...prev, [field]: value }));

            setErrors((prev) => {
                if (!prev[field]) return prev;
                const fieldError = validateField(field, value);
                if (!fieldError) {
                    const { [field]: removed, ...rest } = prev;
                    return rest;
                }
                return { ...prev, [field]: fieldError };
            });
        },
        [validateField],
    );

    const handleCountryChange = useCallback(
        (value) => {
            const selectedOption = countryOptions.find((opt) => opt.value === value?.value);

            setFormData((prev) => ({
                ...prev,
                idCountry: value?.value ? parseInt(value.value, 10) : null,
                country: selectedOption?.label || '',
            }));

            setErrors((prev) => {
                if (!prev.idCountry) return prev;
                if (value?.value) {
                    const { idCountry, ...rest } = prev;
                    return rest;
                }
                return { ...prev, idCountry: getLiteral('helptext_required_field') };
            });
        },
        [countryOptions],
    );

    const handleBlur = useCallback(
        (field) => () => {
            const fieldError = validateField(field, formData[field]);
            setErrors((prev) => ({
                ...prev,
                ...(fieldError ? { [field]: fieldError } : {}),
            }));

            if (!fieldError && errors[field]) {
                setErrors((prev) => {
                    const { [field]: removed, ...rest } = prev;
                    return rest;
                });
            }
        },
        [errors, formData, validateField],
    );

    const handleClose = useCallback(() => {
        setFormData(INITIAL_FORM_STATE);
        setErrors({});
        onCancel?.();
    }, [onCancel]);

    const handleCreate = useCallback(() => {
        const isValid = validateForm();
        if (!isValid) return;
        onCreate?.(formData);
        handleClose();
    }, [formData, handleClose, onCreate, validateForm]);

    const canSubmit = useMemo(() => {
        const hasRequiredValues = REQUIRED_FIELDS.every((field) => {
            const value = formData[field];
            return value !== null && value !== undefined && value !== '';
        });

        const hasEmailErrors =
            !!validateEmail(formData.fromEmail) || !!validateEmail(formData.replyTo);

        return hasRequiredValues && !hasEmailErrors && Object.keys(errors).length === 0;
    }, [errors, formData, validateEmail]);

    const loadOptions = useCallback((value) => {
        return Context.entityMapManager.autocomplete({ address: value }).then((data) => {
            return (
                data?.Result?.map((item) => ({
                    label: item.name,
                    value: item.address,
                    placeId: item.place_id,
                })) || []
            );
        });
    }, []);

    const onUpdateLocation = useCallback(
        ({ lat, lng, address }, firstLoad = false) => {
            getFMAddressFromCoordinates({ lat, lng }, address)
                .then((result) => {
                    const locationName = result?.locationName || result?.googleLocationName || null;
                    setFullAddress(locationName);
                    setAddressFields(locationName ? result : null);
                    setPositionMarker({ lat, lng });
                    if (!isMapPositionUpdated && !firstLoad) {
                        setIsMapPositionUpdated(true);
                    }
                })
                .catch(() => {
                    setFullAddress(null);
                    setAddressFields(null);
                });
        },
        [isMapPositionUpdated],
    );

    const onChangeLocation = useCallback(
        (item) => {
            setLocationValue(item);
            setTimeout(() => setLocationInput(item?.label || ''));
            if (!item) return;

            Context.entityMapManager.getPlaceDetails({ placeId: item.placeId }).then((data) => {
                const location = {
                    lat: data.Result?.FMGeoCodingResults?.[0]?.FMGeometry?.FMLocation?.FMLat,
                    lng: data.Result?.FMGeoCodingResults?.[0]?.FMGeometry?.FMLocation?.FMLng,
                };

                const mapRef = mapComponentRef.current;
                const markerRef = markerComponentRef.current;
                mapRef?.setCenter(location);
                markerRef?.setMarkerPosition?.(location);

                const result = getFMAddressFromGeoCodeResults(data);
                const locationName = result?.locationName || result?.googleLocationName || null;
                setFullAddress(locationName);
                setAddressFields(locationName ? result : null);
                setPositionMarker(location);
                if (!isMapPositionUpdated) setIsMapPositionUpdated(true);
            });
        },
        [isMapPositionUpdated],
    );

    const onAutoCompleteSearch = useCallback(() => {
        const mapRef = mapComponentRef.current;
        if (!mapRef?.getCenter) return;

        const center = mapRef.getCenter();
        const markerRef = markerComponentRef.current;
        if (!markerRef?.setMarkerPosition) return;

        const position = { lat: center?.lat, lng: center?.lng };
        markerRef.setMarkerPosition(position);
        onUpdateLocation(position);
    }, [onUpdateLocation]);

    const onLoadMap = useCallback(() => {
        const lat = formData.latitude;
        const lng = formData.longitude;
        const mapRef = mapComponentRef.current;

        if (lat && lng) {
            const location = { lat, lng };
            setPositionMarker(location);
            onUpdateLocation(location, true);
            mapRef?.setCenter(location);
            return;
        }

        if (mapRef?.getCenter) {
            const center = mapRef.getCenter();
            const location = { lat: center?.lat, lng: center?.lng };
            setPositionMarker(location);
            onUpdateLocation(location, true);
        }
    }, [formData.latitude, formData.longitude, onUpdateLocation]);

    const openMapModal = useCallback(() => {
        setIsGeoLocationModalOpen(true);
        setLocationValue(null);
        setIsMapPositionUpdated(false);
        setLocationInput(formData.address || '');
        loadOptions(formData.address || '').then((options) => setDefaultOptions(options));
    }, [formData.address, loadOptions]);

    const closeMapModal = useCallback(() => {
        setIsGeoLocationModalOpen(false);
    }, []);

    const applyMapLocation = useCallback(() => {
        if (!positionMarker || !addressFields) {
            closeMapModal();
            return;
        }

        const americanFormat = shouldFormatAddressAsAmericanIso(addressFields.countryShort);

        let addressValue = addressFields.shortAddress || '';
        if (addressFields.streetNumber) {
            addressValue = formatAddressFromGeocoder(
                addressFields.shortAddress,
                addressFields.streetNumber,
                '',
                '',
                '',
                '',
                '',
                addressFields.countryShort,
                '',
            );
        }

        let addressDetailValue = '';
        if (addressFields.subpremise) {
            addressDetailValue = `#${addressFields.subpremise}`;
        } else if (addressFields.administrativeAreaLevel3 && !americanFormat) {
            addressDetailValue = addressFields.administrativeAreaLevel3;
        }

        const cityValue = addressFields.locality || '';
        const regionValue = americanFormat ? addressFields.state || '' : addressFields.region || '';
        const postalCodeValue = addressFields.postalCode || '';

        let idCountryValue = null;
        if (addressFields.countryShort) {
            const countryShort = addressFields.countryShort.toLowerCase();
            const match = countriesRaw.find((item) => {
                return (
                    item.striso2?.toLowerCase() === countryShort ||
                    item.striso3?.toLowerCase() === countryShort
                );
            });
            if (match) idCountryValue = parseInt(match.value, 10);
        }

        setFormData((prev) => ({
            ...prev,
            address: addressValue || prev.address,
            addressDetail: addressDetailValue,
            city: cityValue,
            region: regionValue,
            postalCode: postalCodeValue,
            country: addressFields.country || prev.country,
            idCountry: idCountryValue || prev.idCountry,
            latitude: positionMarker.lat,
            longitude: positionMarker.lng,
        }));

        setErrors((prev) => {
            const next = { ...prev };
            ['address', 'city', 'postalCode', 'idCountry'].forEach((key) => {
                if (
                    (key === 'address' && addressValue) ||
                    (key === 'city' && cityValue) ||
                    (key === 'postalCode' && postalCodeValue) ||
                    (key === 'idCountry' && idCountryValue)
                ) {
                    delete next[key];
                }
            });
            return next;
        });

        closeMapModal();
    }, [addressFields, closeMapModal, countriesRaw, positionMarker]);

    const countryValue = useMemo(() => {
        if (!formData.idCountry) return null;
        return countryOptions.find((opt) => opt.value === String(formData.idCountry)) || null;
    }, [countryOptions, formData.idCountry]);

    const mapSelectOverrides = useMemo(() => {
        return {
            'react-select': {
                filterOption: false,
                inputValue: locationInput || '',
                openMenuOnClick: true,
                defaultOptions,
                defaultMenuIsOpen: !isMapPositionUpdated && !!locationInput,
                onInputChange: (text, { action }) => {
                    if (
                        action === 'set-value' ||
                        action === 'input-blur' ||
                        action === 'menu-close'
                    ) {
                        return;
                    }
                    setLocationInput(text);
                },
            },
        };
    }, [defaultOptions, isMapPositionUpdated, locationInput]);

    return (
        <>
            <ModalHoi
                className="marketing-sender-form-modal"
                isOpen={isOpen}
                onRequestClose={onRequestClose}
                onCancel={handleClose}
                onConfirm={handleCreate}
                title={getLiteral('label_create_sender')}
                cancelText={getLiteral('action_cancel')}
                confirmText={getLiteral('action_create')}
                size="large"
                useHeader
                useCornerClose
                isConfirmDisabled={!canSubmit}
            >
                <div className="marketing-sender-form-modal__content">
                    <Text type="body1">
                        {getLiteral('label_marketing_campaigns_create_sender_text_desc')}
                    </Text>

                    <SectionForm
                        title={getLiteral('label_information')}
                        className="marketing-sender-form-modal__section"
                    >
                        <div className="marketing-sender-form-modal__grid marketing-sender-form-modal__grid--two">
                            <Input
                                label={getLiteral('label_from_name')}
                                isRequired
                                isFullWidth
                                value={formData.fromName}
                                onChange={updateField('fromName')}
                                onBlur={handleBlur('fromName')}
                                hint={getLiteral('label_marketing_campaigns_from_name_info')}
                                placeholder={getLiteral('placeholder_enter_sender_name')}
                                error={errors.fromName}
                            />
                            <div className="marketing-sender-form-modal__email-field">
                                <Input
                                    label={getLiteral('label_from_email_address')}
                                    isRequired
                                    isFullWidth
                                    value={formData.fromEmail}
                                    onChange={updateField('fromEmail')}
                                    onBlur={handleBlur('fromEmail')}
                                    hint={getLiteral(
                                        'label_marketing_campaigns_sender_verification_info',
                                    )}
                                    placeholder={getLiteral('placeholder_enter_email_address')}
                                    error={errors.fromEmail}
                                />
                                {isFreeEmailDomain && (
                                    <div className="marketing-sender-form-modal__email-warning">
                                        <div className="marketing-sender-form-modal__email-warning-text">
                                            <Icon name="warning" size="small" />
                                            <span>
                                                {getLiteral(
                                                    'label_marketing_campaigns_sending_from_free_email_address_not_recommended',
                                                )}
                                            </span>
                                        </div>
                                        <Link
                                            className="marketing-sender-form-modal__email-warning-link"
                                            onClick={(event) => event.preventDefault()}
                                            underline
                                        >
                                            {getLiteral('label_learn_more')}
                                        </Link>
                                    </div>
                                )}
                            </div>
                            <Input
                                label={getLiteral('label_reply_to')}
                                isRequired
                                isFullWidth
                                value={formData.replyTo}
                                onChange={updateField('replyTo')}
                                onBlur={handleBlur('replyTo')}
                                hint={getLiteral('label_marketing_campaigns_reply_to_info')}
                                placeholder={getLiteral('placeholder_enter_reply_email')}
                                error={errors.replyTo}
                            />
                            <Input
                                label={getLiteral('label_nickname')}
                                isRequired
                                isFullWidth
                                value={formData.nickname}
                                onChange={updateField('nickname')}
                                onBlur={handleBlur('nickname')}
                                hint={getLiteral('label_marketing_campaigns_nickname_info')}
                                placeholder={getLiteral('placeholder_enter_an_identifier')}
                                error={errors.nickname}
                            />
                        </div>
                    </SectionForm>

                    <SectionForm
                        title={getLiteral('label_address')}
                        className="marketing-sender-form-modal__section"
                    >
                        <div className="marketing-sender-form-modal__grid marketing-sender-form-modal__grid--two">
                            <Input
                                label={getLiteral('label_address')}
                                isRequired
                                isFullWidth
                                value={formData.address}
                                onChange={updateField('address')}
                                onBlur={handleBlur('address')}
                                placeholder={getLiteral('label_address')}
                                error={errors.address}
                            />
                            <Input
                                label={getLiteral('label_address_detail')}
                                isFullWidth
                                value={formData.addressDetail}
                                onChange={updateField('addressDetail')}
                                placeholder={getLiteral('placeholder_enter_address_detail')}
                            />
                        </div>

                        <div className="marketing-sender-form-modal__grid marketing-sender-form-modal__grid--four">
                            <Input
                                label={getLiteral('label_city')}
                                isRequired
                                isFullWidth
                                value={formData.city}
                                onChange={updateField('city')}
                                onBlur={handleBlur('city')}
                                placeholder={getLiteral('label_city')}
                                error={errors.city}
                            />
                            <Input
                                label={getLiteral('label_provinceregion')}
                                isFullWidth
                                value={formData.region}
                                onChange={updateField('region')}
                                placeholder={getLiteral('label_provinceregion')}
                            />
                            <Input
                                label={getLiteral('label_postal_code')}
                                isRequired
                                isFullWidth
                                value={formData.postalCode}
                                onChange={updateField('postalCode')}
                                onBlur={handleBlur('postalCode')}
                                placeholder={getLiteral('label_postal_code')}
                                error={errors.postalCode}
                            />
                            <Select
                                label={getLiteral('label_country')}
                                isRequired
                                isFullWidth
                                options={countryOptions}
                                value={countryValue}
                                onChange={handleCountryChange}
                                placeholder={getLiteral('label_selectone')}
                                error={errors.idCountry}
                                useMenuPortal={false}
                                menuPosition="absolute"
                            />
                        </div>

                        <div
                            className="marketing-sender-form-modal__map-placeholder"
                            onClick={openMapModal}
                        >
                            <img
                                className="marketing-sender-form-modal__map-placeholder-pin"
                                src={`${PATH_IMAGE}img-pin-off.svg`}
                                alt=""
                            />
                            <Button
                                type="primary"
                                className="marketing-sender-form-modal__map-placeholder-button"
                            >
                                {getLiteral('action_mapsearch')}
                            </Button>
                        </div>
                    </SectionForm>
                </div>
            </ModalHoi>

            <Modal
                className="marketing-sender-form-modal__map-modal"
                title={getLiteral('label_set_geolocation')}
                isOpen={isGeoLocationModalOpen}
                onRequestClose={closeMapModal}
                onCancel={closeMapModal}
                onConfirm={applyMapLocation}
                cancelText={getLiteral('action_cancel')}
                confirmText={getLiteral('action_save')}
                isConfirmDisabled={!positionMarker}
                size="large"
                useCornerClose
            >
                <div className="marketing-sender-form-modal__map-content">
                    <SelectHoi
                        placeholder={getLiteral('label_location')}
                        isClearable
                        value={locationValue}
                        loadOptions={loadOptions}
                        onChange={onChangeLocation}
                        overrides={mapSelectOverrides}
                        isFuzzy
                        className="marketing-sender-form-modal__map-select"
                        isFullWidth
                    />
                    <div className="marketing-sender-form-modal__map-canvas">
                        <Map
                            ref={mapComponentRef}
                            onLoadMap={onLoadMap}
                            center={positionMarker}
                            zoom={17}
                            onAutoCompleteSearch={onAutoCompleteSearch}
                        >
                            <GeoLocationFieldMarker
                                ref={markerComponentRef}
                                position={positionMarker}
                                updateLocation={onUpdateLocation}
                            />
                        </Map>
                        {fullAddress && (
                            <div className="marketing-sender-form-modal__map-address">
                                {fullAddress}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    );
};

SenderFormModal.propTypes = {
    isOpen: PropTypes.bool,
    onCancel: PropTypes.func,
    onCreate: PropTypes.func,
    onRequestClose: PropTypes.func,
};

export default memo(SenderFormModal);
